// src/pages/dashboard/Dashboard.js
import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts'

export default function Dashboard() {
  const { profile } = useAuth()
  const [stats, setStats] = useState({ animales: 0, cultivos: 0, vacunas_proximas: 0, saldo: 0 })
  const [finData, setFinData] = useState([])
  const [vacunas, setVacunas] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboard()
  }, [])

  async function loadDashboard() {
    setLoading(true)
    const [animRes, cultRes, vacRes, tranRes] = await Promise.all([
      supabase.from('animales').select('id', { count: 'exact' }).eq('estado', 'activo'),
      supabase.from('cultivos').select('id', { count: 'exact' }).in('estado', ['sembrado', 'en_crecimiento']),
      supabase.from('v_vacunas_proximas').select('*').limit(5),
      supabase.from('transacciones').select('tipo, monto, fecha').gte('fecha', new Date(new Date().getFullYear(), 0, 1).toISOString()),
    ])

    // Agrupar transacciones por mes
    const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']
    const byMonth = Array.from({ length: 12 }, (_, i) => ({ mes: meses[i], ingresos: 0, gastos: 0 }))
    tranRes.data?.forEach(t => {
      const m = new Date(t.fecha).getMonth()
      if (t.tipo === 'ingreso') byMonth[m].ingresos += Number(t.monto)
      else byMonth[m].gastos += Number(t.monto)
    })
    const saldo = (tranRes.data ?? []).reduce((acc, t) => t.tipo === 'ingreso' ? acc + Number(t.monto) : acc - Number(t.monto), 0)

    setStats({
      animales: animRes.count ?? 0,
      cultivos: cultRes.count ?? 0,
      vacunas_proximas: vacRes.data?.length ?? 0,
      saldo,
    })
    setFinData(byMonth.slice(0, new Date().getMonth() + 1))
    setVacunas(vacRes.data ?? [])
    setLoading(false)
  }

  const STAT_CARDS = [
    { label: 'Animales activos', value: stats.animales, icon: '🐄', color: '#16a34a', bg: '#dcfce7' },
    { label: 'Cultivos en curso', value: stats.cultivos, icon: '🌾', color: '#d97706', bg: '#fef3c7' },
    { label: 'Vacunas próximas', value: stats.vacunas_proximas, icon: '💉', color: '#0891b2', bg: '#e0f2fe' },
    { label: 'Saldo del año', value: `$${stats.saldo.toLocaleString('es', { maximumFractionDigits: 0 })}`, icon: '💰', color: '#7c3aed', bg: '#ede9fe' },
  ]

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Buen día, {profile?.nombre} 👋</div>
          <div className="page-subtitle">Resumen operativo — {new Date().toLocaleDateString('es', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
      </div>

      <div className="stats-grid">
        {STAT_CARDS.map(card => (
          <div className="stat-card" key={card.label}>
            <div className="stat-icon" style={{ background: card.bg }}>
              <span style={{ fontSize: 22 }}>{card.icon}</span>
            </div>
            <div>
              <div className="stat-value">{loading ? '—' : card.value}</div>
              <div className="stat-label">{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div className="card-title">Ingresos vs Gastos (año actual)</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={finData} barGap={4}>
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={60}
                tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v, n) => [`$${v.toLocaleString()}`, n === 'ingresos' ? 'Ingresos' : 'Gastos']} />
              <Bar dataKey="ingresos" fill="#22c55e" radius={[4,4,0,0]} />
              <Bar dataKey="gastos" fill="#f87171" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">Vacunas próximas (30 días)</div>
          </div>
          {vacunas.length === 0 ? (
            <div className="empty-state">
              <div>✅</div>
              <p>No hay vacunas pendientes</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Animal</th>
                    <th>Vacuna</th>
                    <th>Días</th>
                  </tr>
                </thead>
                <tbody>
                  {vacunas.map(v => (
                    <tr key={v.id}>
                      <td><strong>{v.animal_codigo}</strong> {v.animal_nombre && `(${v.animal_nombre})`}</td>
                      <td>{v.vacuna}</td>
                      <td>
                        <span className={`badge ${v.dias_restantes <= 7 ? 'badge-red' : 'badge-amber'}`}>
                          {v.dias_restantes}d
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
